import { Link, useParams } from "react-router-dom";
import { Check, Users, ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import { PageHeader } from "@/components/PageHeader";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import { AppointmentCTA } from "@/components/AppointmentCTA";
import { Icon } from "@/utils/icons";
import { SITE } from "@/config/site";
import { getServiceBySlug, SERVICES } from "@/data/services";
import { WA_MESSAGES } from "@/utils/whatsapp";

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = getServiceBySlug(slug);

  if (!service) {
    return (
      <section className="py-24 text-center" data-testid="service-not-found">
        <h1 className="font-heading text-3xl font-bold text-navy-900">Service not found</h1>
        <p className="mt-3 text-slate-600">The service you are looking for does not exist.</p>
        <Link to="/services" className="mt-6 inline-flex rounded-full bg-navy-800 px-6 py-3 text-sm font-semibold text-white" data-testid="service-not-found-back">
          View all services
        </Link>
      </section>
    );
  }

  const others = SERVICES.filter((s) => s.slug !== slug).slice(0, 4);

  return (
    <>
      <Seo
        title={`${service.name} — ${SITE.name}`}
        siteName={SITE.name}
        description={`${service.name} at ${SITE.name}, Kasdol. ${service.short}`}
      />
      <PageHeader
        title={service.name}
        description={service.short}
        crumbs={[{ label: "Services", to: "/services" }, { label: service.name }]}
      />

      <section className="py-14 sm:py-20" data-testid="service-detail-section">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 sm:px-6 lg:grid-cols-3 lg:px-8">
          <div className="lg:col-span-2">
            <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-navy-800 text-white">
              <Icon name={service.icon} className="h-7 w-7" aria-hidden="true" />
            </span>
            <h2 className="mt-6 font-heading text-2xl font-bold text-navy-900">About this service</h2>
            <p className="mt-4 leading-relaxed text-slate-600">{service.description}</p>

            <h2 className="mt-10 flex items-center gap-2 font-heading text-2xl font-bold text-navy-900">
              <Users className="h-5 w-5 text-emerald-600" aria-hidden="true" /> Who is it for?
            </h2>
            <ul className="mt-5 space-y-3">
              {service.whoFor.map((w) => (
                <li key={w} className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" aria-hidden="true" /> {w}
                </li>
              ))}
            </ul>

            <div className="mt-10 rounded-2xl border border-emerald-200 bg-emerald-50 p-6" data-testid="service-cta-box">
              <h3 className="font-heading text-lg font-semibold text-navy-900">Interested in {service.name}?</h3>
              <p className="mt-2 text-sm text-slate-600">
                Send us a WhatsApp message or request an appointment — the hospital team will confirm the details.
              </p>
              <div className="mt-4 flex flex-col gap-3 sm:flex-row">
                <WhatsAppButton message={`${WA_MESSAGES.general} I am interested in: ${service.name}.`} testId="service-whatsapp-button">
                  Enquire on WhatsApp
                </WhatsAppButton>
                <Link
                  to="/appointments"
                  className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-navy-200 bg-white px-6 py-3 text-sm font-semibold text-navy-800 transition-colors hover:border-navy-800"
                  data-testid="service-appointment-link"
                >
                  Book an Appointment <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </Link>
              </div>
            </div>
          </div>

          <aside className="lg:sticky lg:top-32 lg:self-start">
            <div className="rounded-2xl border border-slate-200 bg-white p-6">
              <h3 className="font-heading text-base font-semibold text-navy-900">Other Services</h3>
              <ul className="mt-4 space-y-1">
                {others.map((s) => (
                  <li key={s.slug}>
                    <Link
                      to={`/services/${s.slug}`}
                      className="flex items-center justify-between rounded-xl px-3 py-2.5 text-sm text-slate-600 transition-colors hover:bg-navy-50 hover:text-navy-800"
                      data-testid={`service-related-${s.slug}`}
                    >
                      {s.name} <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </section>

      <AppointmentCTA />
    </>
  );
}
